// ─── CHEMICAL HOW-TO GUIDE ────────────────────────────────────────────────────
// Source: Mint Detail — Chemical How-To Guide. Shown in-app so the crew can
// check when to use / how to mix / how to apply every product.
// Ratios are product : water — "1:5" = 1 part product to 5 parts water.

export const GUIDE_INTRO = "Company products only in the vans — nothing from home. Pre-mix labeled bottles at fixed ratios each day. All ratios are product : water (1:5 = 1 part product to 5 parts water).";

export const DECISION_INTERIOR = [
  { need: "Clean hard surfaces — dash, doors, console, vinyl, plastic, leather", grab: "Pol Star", mix: "1:10 (1:5 heavy)" },
  { need: "Clean carpet & upholstery", grab: "Carpet Bomber", mix: "1:8 (1:5 moderate)" },
  { need: "Remove a stain — food, drink, biological", grab: "Terminator", mix: "RTU (spot only)" },
  { need: "Degrease greasy mats, plastics, pedals", grab: "Carpet Bomber (stronger)", mix: "1:3 to 1:2" },
  { need: "Dress / finish plastic & vinyl trim", grab: "D170", mix: "1:4" },
  { need: "Clean glass", grab: "Sprayway", mix: "RTU" },
];

export const DECISION_EXTERIOR = [
  { need: "Clean wheels & tires", grab: "Brake Buster", mix: "1:5 (stronger for heavy)" },
  { need: "Dress tires & trim", grab: "D170", mix: "1:4 satin / 1:1 gloss" },
  { need: "Maintenance wash (membership)", grab: "Superior Waterless", mix: "1:5" },
  { need: "Clean glass", grab: "Sprayway", mix: "RTU" },
];

export const CLEAN_VS_DRESS = {
  headline: "Clean first, then dress. (The #1 mix-up.)",
  points: [
    "Koch Chemie Pol Star = CLEANER — it removes dirt. Use on any dirty interior surface.",
    "Meguiar's D170 = DRESSING — it does NOT clean; it protects and adds a satin finish. Use it after cleaning, on plastic & vinyl trim only.",
    "Dirty surface → Pol Star. Clean surface you want finished/protected → D170.",
    "Never dress a dirty surface (you seal the dirt in). Never try to 'clean' with D170.",
    "Outside, same rule: Brake Buster cleans the wheel & tire; D170 dresses the tire. Clean and dry first.",
  ],
};

export const ORDER_INTERIOR = [
  "Vacuum.",
  "Terminator — spot any organic stains: dwell, agitate, blot.",
  "Carpet Bomber — carpets & upholstery: agitate, extract.",
  "Pol Star — wipe down all hard surfaces (dash, doors, console, trim, leather).",
  "D170 (1:4) — dress plastic & vinyl trim, thin and leveled. NOT leather, steering wheel, or pedals.",
  "Sprayway — glass, last.",
];

export const ORDER_EXTERIOR = [
  "Brake Buster — wheels & tires: agitate, rinse/wipe, let the tire dry.",
  "Wash the car (Superior Waterless for maintenance washes).",
  "D170 — dress tires & trim (1:4 satin default), thin, leveled; let it set before the car moves.",
  "Sprayway — exterior glass, last.",
];

export const PRODUCTS = [
  {
    name: "P&S Terminator",
    tag: "Enzyme spot treatment",
    color: "#8B5CF6",
    when: "Organic stains only — food, drink, biological spots on carpet & upholstery. A spot treatment, not a general cleaner.",
    mix: "Ready to use — never dilute. It's the priciest per spray, so spot-treat stains; don't soak whole carpets.",
    how: "Spray on the stain → dwell 1–2 min → agitate with a brush → blot or extract → follow with Carpet Bomber for the whole area. Gloves on.",
  },
  {
    name: "P&S Carpet Bomber",
    tag: "Carpet prespray + our degreaser",
    color: "#F59E0B",
    when: "Carpet & upholstery cleaning, and any greasy interior job — dirty mats, plastics, pedals. This is our degreaser; when it's greasy, mix stronger rather than reaching for anything harsher.",
    mix: "1:8 light / normal prespray · 1:5 moderate soil · 1:3 heavy grease (mats, plastics, pedals).",
    how: "Spray, agitate with a brush, then extract (carpet) or wipe/rinse (mats & plastics). Pedals: 1:5 on a brush, then wipe clean and dry so they aren't slick.",
  },
  {
    name: "Koch Chemie Pol Star",
    tag: "Interior CLEANER",
    color: "#0EA5E9",
    when: "Cleaning any interior hard surface — dash, doors, console, vinyl, plastic, trim, and leather. Your everyday interior cleaner. It cleans; it does not dress.",
    mix: "1:10 general interior · 1:5 heavy soil (dirty plastics & inserts).",
    how: "Pre-mix labeled bottles at the start of the day. Spray on a towel or the surface, then wipe. Leather: clean with Pol Star; condition separately on premium cars. Not cutting it? Step up to 1:5 — don't reach for harsher.",
  },
  {
    name: "P&S Brake Buster",
    tag: "Wheel & tire cleaner",
    color: "#EF4444",
    when: "Cleaning wheels and tires — brake dust and road film. Non-acid, safe on all wheel finishes.",
    mix: "1:5 regular maintenance · up to 1:1 heavy brake dust (mix stronger only when needed).",
    how: "Spray on a cool, dry wheel & tire → agitate with a brush → rinse or wipe. Clean and dry the tire BEFORE you dress it with D170.",
  },
  {
    name: "Meguiar's D170 Hyper Dressing",
    tag: "Tire & trim DRESSING",
    color: "#0D9488",
    when: "Dressing (not cleaning) tires, exterior trim, plastic, rubber, wheel wells, and interior plastic/vinyl trim. Always on a clean, dry surface — the last step, after cleaning.",
    mix: "1:4 SATIN / factory look — DEFAULT for every car, premium included · 1:2 semi-gloss · 1:1 HIGH GLOSS wet look (customer request). Keep two labeled bottles: 1:4 satin and 1:1 gloss.",
    how: "Surface clean & dry → apply thin with an applicator/brush, let it dwell → (high gloss: second coat after it sets) → level the excess with a towel → let it fully set before the car moves so it doesn't sling.",
    never: "NEVER on leather (use a conditioner), steering wheels, or pedals — slippery contact points are a safety issue. Don't let it sling onto paint.",
  },
  {
    name: "Sprayway Glass Cleaner",
    tag: "Glass — every plan",
    color: "#3B82F6",
    when: "All glass and mirrors, inside and out. Every service.",
    mix: "Ready to use (aerosol). Light foam does the work; don't over-spray.",
    how: "Light foam on the glass or the towel → wipe, then flip to a dry side and buff → do glass last.",
    never: "Dedicated low-lint / waffle glass towel only. Plush towels leave lint and streaks.",
  },
  {
    name: "Superior Waterless Wash & Wax",
    tag: "Membership maintenance wash",
    color: "#16A34A",
    when: "Exterior maintenance washes on LIGHTLY soiled cars (membership visits). Cleans and leaves a wax gloss in one step.",
    mix: "1:5 light maintenance (default) · 1:3 dirtier cars.",
    how: "Pre-mix a labeled bottle → work one panel at a time, spray generously → wipe with plush microfiber, flip often → buff dry with a second towel.",
    never: "Light soil only. If a car is heavily soiled or gritty, DON'T waterless it — grit drags across paint and causes swirls. Plush (green) towels only, flip constantly.",
  },
];

export const FAQS = [
  { q: "The Meguiar's or the Koch Chemie for the interior?", a: "Koch Chemie Pol Star is the interior CLEANER — that's what you clean with. Meguiar's D170 is a DRESSING, not a cleaner — it goes on after cleaning, on plastic/vinyl trim, to add a satin finish. Clean with Pol Star, then dress trim with D170 if the car calls for it." },
  { q: "Carpet Bomber isn't cutting the grease — do we need a stronger degreaser?", a: "No. Carpet Bomber IS the degreaser — mix it stronger (1:3 or 1:2). We don't add harsher chemicals; a stronger dilution of what we stock does the job and stays safe on paint and coated surfaces." },
  { q: "Terminator or Carpet Bomber for the carpet?", a: "Both, in order. Terminator spot-treats the stains first (ready to use, targeted). Carpet Bomber cleans the whole area after (diluted, extracted). Terminator is expensive per spray, so it's for spots — not for soaking a whole carpet." },
  { q: "Can I dress the tire before cleaning it?", a: "No. Brake Buster cleans, D170 dresses. Clean the tire, let it dry, then dress. Dressing over a dirty or wet tire causes browning and uneven shine." },
];

export const CHEAT_SHEET = [
  { product: "Terminator", ratio: "RTU", job: "Spot-treat organic stains" },
  { product: "Carpet Bomber", ratio: "1:8 / 1:5 / 1:3", job: "Prespray → moderate → heavy grease" },
  { product: "Pol Star", ratio: "1:10 / 1:5", job: "General → heavy interior cleaning" },
  { product: "Brake Buster", ratio: "1:5 → 1:1", job: "Wheels & tires (stronger for brake dust)" },
  { product: "D170 Dressing", ratio: "1:4 / 1:1", job: "Satin (default) / gloss (on request)" },
  { product: "Sprayway", ratio: "RTU", job: "All glass, last step" },
  { product: "Superior Waterless", ratio: "1:5 / 1:3", job: "Light → dirtier maintenance wash" },
];
